import { ART_SCHEMA, isArtFormat, normalizeDocument, validateDocument } from '../model/schema';

// What "Adicionar modelo" reads: the JSON of a template (as `GET /art-templates/{id}` answers
// it, with its `document`), a bare art document (`ginastee.art/1`), or a template file of the
// core catalog (one document per locale, as the studio-art-template skill writes it; a card
// with such a `document` works too). It turns the file into what the host's `create`
// takes (name, description, category, tags, document) or says why it cannot: `json` (not
// JSON), `shape` (no art document in it), `format` (a document of another format) or
// `invalid` (the document breaks the model, with the first paths).
export const TEMPLATE_FILE_MAX_BYTES = 5 * 1024 * 1024;
const LOCALES = { pt: 'pt_BR', en: 'en_US', es: 'es_ES' };

const isDocument = value => !!value && typeof value === 'object' && value.schema === ART_SCHEMA;
// A catalog value is either the text or the text per locale.
const textOf = (value, locale) => {
  if (typeof value === 'string') return value.trim() || null;
  if (!value || typeof value !== 'object') return null;
  return [value[locale], value.pt_BR, ...Object.values(value)].find(item => typeof item === 'string' && item.trim())?.trim() || null;
};
// A file named after a template id (`sat-bubl0001.json`) gives no name: the creator writes one.
const titleOf = fileName => {
  const base = (fileName || '').replace(/\.json$/i, '');
  return /^sat-[0-9a-z]+$/i.test(base) ? '' : base.replace(/[-_]+/g, ' ').trim();
};

// What "Exportar JSON" writes (the template generator): the template as `create` takes it
// (name, description, category, tags, document), plus its format, ratio and page count for
// whoever opens the file. `readTemplateFile` reads it back, and so does "Adicionar modelo".
export function writeTemplateFile({ name, description = null, category_key: categoryKey = null, tags = [], document }) {
  const doc = normalizeDocument(document);
  return JSON.stringify({ name: (name || '').trim() || null, description: description || null, category_key: categoryKey, tags,
    format: doc.format, aspect_ratio: doc.aspect_ratio, page_count: doc.pages.length, document: doc }, null, 2);
}
// "Lista numerada · verão" → `lista-numerada-verao.json`.
export const templateFileName = name => `${(name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'modelo'}.json`;

export function readTemplateFile(source, { format = null, fileName = '', language = 'pt' } = {}) {
  let data;
  try { data = JSON.parse(source); } catch { return { error: 'json' }; }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return { error: 'shape' };
  const locale = LOCALES[(language || '').slice(0, 2)] || 'pt_BR';
  // A document, or one per locale (the creator's language first).
  const pick = value => (isDocument(value) ? value : value && typeof value === 'object' ? [value[locale], value.pt_BR, ...Object.values(value)].find(isDocument) : null);
  const nested = isDocument(data) ? null : pick(data.document);
  const meta = nested ? data : {};
  const document = isDocument(data) ? data : nested || pick(data);
  if (!document) return { error: 'shape' };
  if (format && isArtFormat(document.format) && document.format !== format) return { error: 'format', format: document.format };
  const problems = validateDocument(document);
  if (problems.length) return { error: 'invalid', paths: problems.slice(0, 3).map(problem => problem.path) };
  const tags = Array.isArray(meta.tags) ? meta.tags.filter(tag => typeof tag === 'string' && tag.trim()).map(tag => tag.trim().slice(0, 40)).slice(0, 10) : [];
  return { template: {
    name: (textOf(meta.name, locale) || titleOf(fileName)).slice(0, 80),
    description: textOf(meta.description, locale)?.slice(0, 280) || null,
    category_key: meta.category_key || meta.category || null,
    tags,
    document: normalizeDocument(document),
  } };
}
